import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Draggable } from 'react-beautiful-dnd';
import {
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip
} from '@material-ui/core';
import {
  Add as AddIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon
} from '@material-ui/icons';

import { TestStep } from './TestStep.interface';
import { useTimeout } from '../../../../common/timeout.hook';

const StyledTableRow = styled(TableRow)`
  background-color: #fff;
  .actions {
    display: flex;
    align-items: center;
    justify-content: center;
    .MuiIconButton-root:not(:first-child) {
      margin-left: 2px;
    }
  }
`;

export interface TestStepItemProps {
  testStep: TestStep;
  index: number;
  disabled: boolean;
  onChange?: (updateTestStep: Partial<TestStep>) => void;
  onAddStep: (testStep: TestStep, index?: number) => void;
  onRemoveStep?: () => void;
  setDraggingHeight?: (height: number | undefined) => void;
}

const TestStepItem: React.FC<TestStepItemProps> = (
  props: TestStepItemProps
) => {
  const {
    testStep: initialTestStep,
    index,
    disabled,
    onChange,
    onAddStep,
    onRemoveStep,
    setDraggingHeight
  } = props;
  const [debounceDrag] = useTimeout();
  const elementRef = useRef<HTMLElement | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [testStep, setTestStep] = useState<TestStep>(initialTestStep);
  const [draggingHeight, setDraggingHeightState] = useState<
    number | undefined
  >();

  const onChangeValue = (updateTestStep: Partial<TestStep>) => {
    setIsDirty(true);
    setTestStep({ ...testStep, ...updateTestStep });
  };

  // only update testStep on blur
  const onUpdate = () => {
    setIsDirty(false);
    if (isDirty) {
      onChange?.(testStep);
    }
  };

  useEffect(() => {
    setTestStep(initialTestStep);
  }, [initialTestStep]);

  useEffect(() => {
    setDraggingHeight?.(draggingHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingHeight]);

  return (
    <Draggable
      index={index}
      key={`${testStep.stepNo}-${index}`}
      draggableId={`step-${testStep.stepNo}`}
    >
      {(provided, snapshot) => {
        debounceDrag(() => {
          const height: number | undefined = elementRef.current?.offsetHeight;
          const isDragging: boolean = typeof draggingHeight === 'number';
          isDragging !== snapshot.isDragging &&
            setDraggingHeightState(snapshot.isDragging ? height : undefined);
        });

        return (
          <StyledTableRow
            {...provided.draggableProps}
            ref={(ref: HTMLElement | null) => {
              elementRef.current = ref;
              provided.innerRef(ref);
            }}
          >
            {!disabled ? (
              <TableCell>
                <Tooltip
                  title={
                    isDirty
                      ? 'Step data not yet saved'
                      : 'Click and drag to reoder'
                  }
                >
                  <span>
                    <IconButton
                      size='small'
                      // only allow dragging once step is saved
                      disabled={isDirty}
                      {...provided.dragHandleProps}
                    >
                      <DragIndicatorIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            ) : (
              <TableCell />
            )}
            <TableCell>{testStep.stepNo}</TableCell>
            {/* actions */}
            <TableCell>
              <TextField
                multiline
                placeholder='Description'
                value={testStep.description || ''}
                onBlur={onUpdate}
                disabled={disabled}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  onChangeValue({ description: event.target.value });
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                multiline
                placeholder='Sample Data'
                disabled={disabled}
                value={testStep.sampleData || ''}
                onBlur={onUpdate}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  onChangeValue({ sampleData: event.target.value });
                }}
              />
            </TableCell>
            <TableCell>
              <TextField
                multiline
                placeholder='Expected Result'
                value={testStep.expectedResult || ''}
                onBlur={onUpdate}
                disabled={disabled}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                  onChangeValue({ expectedResult: event.target.value });
                }}
              />
            </TableCell>
            <TableCell>
              {!disabled && (
                <div className='actions'>
                  {index === 0 && (
                    <Tooltip title='Add Step above'>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={() => onAddStep({ stepNo: index }, index)}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title='Remove Step'>
                    <IconButton
                      size='small'
                      color='inherit'
                      onClick={onRemoveStep}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Add Step below'>
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={() =>
                        onAddStep({ stepNo: index + 1 }, index + 1)
                      }
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </TableCell>
          </StyledTableRow>
        );
      }}
    </Draggable>
  );
};

export default TestStepItem;
